import React, { useState } from "react";
import { Box, Button, Chip, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { PersonRef } from "../types";
import PeoplePicker from "./PeoplePicker";

interface PeopleFieldProps {
  label: string;
  required?: boolean;
  value: PersonRef[];
  emptyLabel: string;
  pickerTitle: string;
  corporateId: string | number;
  roleFilter: string;
  error?: boolean;
  onChange: (people: PersonRef[]) => void;
}

const PeopleField: React.FC<PeopleFieldProps> = ({
  label,
  required,
  value,
  emptyLabel,
  pickerTitle,
  corporateId,
  roleFilter,
  error,
  onChange,
}) => {
  const { t } = useTranslation("session-wizard");
  const theme = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleRemove = (id: string | number) => {
    onChange(value.filter((p) => String(p.id) !== String(id)));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {label}
          {required ? " *" : ""}
        </Typography>
        <Button
          variant="text"
          startIcon={<Plus size={14} />}
          onClick={() => setPickerOpen(true)}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            color: theme.palette.primary.main,
          }}
        >
          {t("actions.add")}
        </Button>
      </Box>

      {value.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 2,
            fontSize: "13px",
            color: theme.palette.text.tertiary,
            borderRadius: "8px",
            border: `1px ${error ? "solid" : "dashed"} ${
              error ? theme.palette.error.main : theme.palette.divider
            }`,
          }}
        >
          {emptyLabel}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            padding: "10px",
            borderRadius: "8px",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {value.map((p) => (
            <Chip
              key={String(p.id)}
              label={p.label}
              onDelete={() => handleRemove(p.id)}
              sx={{ borderRadius: "8px", fontWeight: 600 }}
            />
          ))}
        </Box>
      )}

      {pickerOpen && (
        <PeoplePicker
          title={pickerTitle}
          corporateId={corporateId}
          roleFilter={roleFilter}
          initialSelected={value}
          onClose={() => setPickerOpen(false)}
          onConfirm={onChange}
        />
      )}
    </Box>
  );
};

export default PeopleField;
